import React, { useState } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_PB, s_GC, s_PL } from '../../shared/styles';
import { t } from '../../shared/i18n';

function WeekPlanOnboard({lang,weekIntention,weekAgenda,setWeekAgenda,onDone}) {
  const nl=lang==="nl";
  const days=["mon","tue","wed","thu","fri","sat","sun"].map(d=>t(`day.${d}`,lang));

  const QUICK_ACTS=[
    {id:"work",    icon:"💼",label:"Work",      labelNL:"Werk",      mental:75,physical:15,type:"activity"},
    {id:"school",  icon:"📚",label:"School",    labelNL:"School",    mental:80,physical:10,type:"activity"},
    {id:"sport",   icon:"🏋️",label:"Sport",     labelNL:"Sport",     mental:30,physical:85,type:"exercise"},
    {id:"walk",    icon:"🚶",label:"Walk",       labelNL:"Wandelen",  mental:15,physical:40,type:"exercise"},
    {id:"social",  icon:"👥",label:"Social",    labelNL:"Sociaal",   mental:55,physical:20,type:"activity"},
    {id:"rest",    icon:"🧘",label:"Rest",       labelNL:"Rust",      mental:45,physical:5, type:"rest"},
    {id:"creative",icon:"🎨",label:"Creative",  labelNL:"Creatief",  mental:60,physical:5, type:"activity"},
    {id:"breathing",icon:"🌬️",label:"Breathing",labelNL:"Ademhaling",mental:35,physical:10,type:"rest"},
    {id:"meditation",icon:"🧘",label:"Meditation",labelNL:"Meditatie",mental:45,physical:5,type:"rest"},
  ];

  const [selDay,setSelDay]=useState(null);
  const intentionLower=(weekIntention||"").toLowerCase();

  const exerciseDays=[0,2,4];
  const restDays=[1,3,6];

  const preferSport=/sport|gym|train|actief|active|run|hardloop|lift|strength/.test(intentionLower);
  const exerciseId=preferSport?"sport":"walk";

  const preferMeditation=/medit|mindful|stilte|quiet|present|bewust|conscious/.test(intentionLower);
  const restId=preferMeditation?"meditation":"rest";

  const suggExercise=exerciseDays.map(day=>({day,id:exerciseId,type:"exercise"}));
  const suggRest=restDays.map(day=>({day,id:restId,type:"rest"}));

  if(/social|vrienden|friends|connect|samen/.test(intentionLower)){suggExercise.push({day:5,id:"social",type:"activity"});}
  if(/werk|work|produc|focus|project/.test(intentionLower)){[0,1,2,3,4].forEach(d=>{if(!suggExercise.find(s=>s.day===d))suggExercise.push({day:d,id:"work",type:"activity"});});}
  if(/school|stud|leer|learn/.test(intentionLower)){[0,1,2,3,4].forEach(d=>{if(!suggExercise.find(s=>s.day===d))suggExercise.push({day:d,id:"school",type:"activity"});});}

  const allSugg=[...suggExercise,...suggRest];

  const applySuggestions=()=>{
    const agenda={...weekAgenda};
    allSugg.forEach(({day,id})=>{
      const found=QUICK_ACTS.find(a=>a.id===id);if(!found)return;
      if(!agenda[day])agenda[day]=[];
      if(!agenda[day].some(i=>i.id===found.id))
        agenda[day]=[...agenda[day],{...found,duration:60,startTime:"09:00",id:Date.now()+day+Math.random()}];
    });
    for(let d=0;d<6;d++){
      const items=agenda[d]||[];
      const hasHeavy=items.some(i=>i.mental>65||i.physical>65);
      if(hasHeavy){
        const nd=d+1;
        if(!agenda[nd])agenda[nd]=[];
        const breath=QUICK_ACTS.find(a=>a.id==="breathing");
        if(breath&&!agenda[nd].some(i=>i.type==="rest"))
          agenda[nd]=[...agenda[nd],{...breath,duration:10,startTime:"07:30",id:Date.now()+nd+Math.random()}];
      }
    }
    setWeekAgenda(agenda);
  };

  const toggleAct=(dayIdx,actId)=>{
    const found=QUICK_ACTS.find(a=>a.id===actId);if(!found)return;
    const dayItems=weekAgenda[dayIdx]||[];
    const exists=dayItems.some(i=>i.id===found.id);
    if(exists){setWeekAgenda({...weekAgenda,[dayIdx]:dayItems.filter(i=>i.id!==found.id)});}
    else{setWeekAgenda({...weekAgenda,[dayIdx]:[...dayItems,{...found,duration:60,startTime:"09:00",id:Date.now()+Math.random()}]});}
  };

  const totalItems=Object.values(weekAgenda).flat().length;
  const alreadyApplied=totalItems>0;

  return(
    <div style={{minHeight:"100vh",background:`linear-gradient(160deg,${_T.bg},${_T.bg2})`,display:"flex",flexDirection:"column",padding:24,paddingTop:48}}>
      <style>{CSS}</style>
      <div style={{fontSize:9,color:_T.accent,letterSpacing:3,fontWeight:600,marginBottom:6}}>WEEK PLANNING</div>
      <div style={{fontFamily:"Georgia,serif",fontSize:22,color:C.white,marginBottom:6,lineHeight:1.3}}>{t("weekplan.title",lang)}</div>
      {weekIntention&&<div style={{color:C.silver,fontSize:13,fontStyle:"italic",marginBottom:18}}>"{weekIntention}"</div>}

      {/* ── Smart schedule card ── */}
      <div style={{...s_GC({padding:"16px",marginBottom:16}),borderTop:`1.5px solid ${_T.accent}66`}}>
        <div style={{fontSize:12,color:_T.accent,fontWeight:700,letterSpacing:2,marginBottom:10}}>
          ✨ {t("weekplan.recommended",lang)}
        </div>

        {/* Exercise row */}
        <div style={{marginBottom:10}}>
          <div style={{fontSize:11,color:C.silver,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:14}}>🏃</span>
            <span style={{fontWeight:600,color:C.white}}>{t("weekplan.exercise_label",lang)}</span>
            <span style={{color:C.silver}}>—</span>
            <span>{exerciseDays.map(d=>days[d]).join(", ")}</span>
          </div>
          <div style={{display:"flex",gap:4}}>
            {exerciseDays.map(d=>{
              const act=QUICK_ACTS.find(a=>a.id===exerciseId);
              return(
                <div key={d} style={{flex:1,background:`${_T.accent}15`,border:`1px solid ${_T.accent}44`,borderRadius:10,padding:"6px 4px",textAlign:"center"}}>
                  <div style={{fontSize:11,color:C.silver,marginBottom:2}}>{days[d]}</div>
                  <div style={{fontSize:16}}>{act?.icon}</div>
                  <div style={{fontSize:9,color:_T.accent,marginTop:1}}>{nl?act?.labelNL:act?.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Rest row */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,color:C.silver,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:14}}>🌿</span>
            <span style={{fontWeight:600,color:C.teal}}>{t("weekplan.rest_rec",lang)}</span>
            <span style={{color:C.silver}}>—</span>
            <span>{restDays.map(d=>days[d]).join(", ")}</span>
          </div>
          <div style={{display:"flex",gap:4}}>
            {restDays.map(d=>{
              const act=QUICK_ACTS.find(a=>a.id===restId);
              return(
                <div key={d} style={{flex:1,background:`${C.teal}12`,border:`1px solid ${C.teal}44`,borderRadius:10,padding:"6px 4px",textAlign:"center"}}>
                  <div style={{fontSize:11,color:C.silver,marginBottom:2}}>{days[d]}</div>
                  <div style={{fontSize:16}}>{act?.icon}</div>
                  <div style={{fontSize:9,color:C.teal,marginTop:1}}>{nl?act?.labelNL:act?.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        <button
          style={s_PB({fontSize:12,padding:"9px 16px",width:"100%",opacity:alreadyApplied?0.55:1})}
          onClick={applySuggestions}
        >
          {alreadyApplied ? t("weekplan.reapply",lang) : t("weekplan.apply",lang)}
        </button>
      </div>

      {/* ── Day selector ── */}
      <div style={{fontSize:11,color:C.silver,marginBottom:8}}>{t("weekplan.manual",lang)}</div>
      <div style={{display:"flex",gap:4,marginBottom:14,overflowX:"auto"}}>
        {days.map((d,i)=>{
          const count=(weekAgenda[i]||[]).length;
          const hasExercise=(weekAgenda[i]||[]).some(a=>a.type==="exercise");
          const hasRest=(weekAgenda[i]||[]).some(a=>a.type==="rest");
          return(
            <button key={i} onClick={()=>setSelDay(selDay===i?null:i)}
              style={{flex:"0 0 auto",width:42,background:selDay===i?`${_T.accent}18`:_T.bg3,
                borderTop:`1.5px solid ${selDay===i?_T.accent:_T.cardBorderTop}`,
                borderBottom:`1px solid ${_T.cardBorderBot}`,borderLeft:"1px solid rgba(255,255,255,0.03)",
                borderRight:"1px solid rgba(255,255,255,0.03)",borderRadius:12,padding:"7px 4px",cursor:"pointer",textAlign:"center"}}>
              <div style={{fontSize:13,color:selDay===i?_T.accent:C.silver,fontWeight:selDay===i?700:400}}>{d}</div>
              <div style={{display:"flex",justifyContent:"center",gap:2,marginTop:3}}>
                {hasExercise&&<div style={{width:5,height:5,borderRadius:"50%",background:_T.accent}}/>}
                {hasRest&&<div style={{width:5,height:5,borderRadius:"50%",background:C.teal}}/>}
                {count>0&&!hasExercise&&!hasRest&&<div style={{width:5,height:5,borderRadius:"50%",background:C.silver}}/>}
              </div>
            </button>
          );
        })}
      </div>

      {selDay!==null&&(
        <div style={s_GC({marginBottom:12,padding:14})}>
          <div style={{fontSize:12,color:C.silver,marginBottom:10,fontWeight:600}}>{days[selDay]} — {t("weekplan.tap_toggle",lang)}</div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {QUICK_ACTS.map(act=>{
              const active=(weekAgenda[selDay]||[]).some(i=>i.id===act.id);
              const col=act.type==="rest"?C.teal:act.type==="exercise"?_T.accent:C.silver2;
              return(
                <button key={act.id} onClick={()=>toggleAct(selDay,act.id)}
                  style={{...s_PL(active,col),display:"flex",alignItems:"center",gap:4}}>
                  <span>{act.icon}</span><span>{nl?act.labelNL:act.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div style={{marginTop:"auto",paddingTop:20}}>
        {totalItems>0&&(
          <div style={{fontSize:12,marginBottom:12,textAlign:"center",display:"flex",justifyContent:"center",gap:16}}>
            <span style={{color:_T.accent}}>🏃 {Object.values(weekAgenda).flat().filter(a=>a.type==="exercise").length} {t("dash.workouts",lang)}</span>
            <span style={{color:C.teal}}>🌿 {Object.values(weekAgenda).flat().filter(a=>a.type==="rest").length} {t("dash.rest_moments",lang)}</span>
          </div>
        )}
        <button style={s_PB({width:"100%",fontSize:15})} onClick={onDone}>
          {totalItems>0 ? t("weekplan.saved",lang) : t("common.skip",lang)}
        </button>
        <div style={{display:"flex",justifyContent:"center",gap:20,paddingTop:16}}>
          <a href="/privacy" style={{color:"#3a4a62",fontSize:11,textDecoration:"none"}}>Privacy Policy</a>
          <a href="/terms" style={{color:"#3a4a62",fontSize:11,textDecoration:"none"}}>Terms &amp; Conditions</a>
        </div>
      </div>
    </div>
  );
}

export default WeekPlanOnboard;
